import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { toast } from 'react-toastify';
import { fetchAllMeetingMeals, fetchBulkCreateMealsStatus } from '../../controllers/meetingMealController';
import { fetchWeeklyMeetingConfig } from '../../controllers/meetingsController';
import {
  BULK_MEALS_JOB_STORAGE_KEY,
  MeetingMealStatusNames,
  getMeetingMealStatusChipSx,
} from './meetingMealsConstants';
import { formatMealDate, formatMealMoney, formatPeriodLabel } from './meetingMealsFormUtils';
import WeeklyMeetingConfigDialog from './components/WeeklyMeetingConfigDialog';
import CreateMemberMealDialog from './components/CreateMemberMealDialog';
import BulkCreateMealsDialog from './components/BulkCreateMealsDialog';

const PAGE_SIZE = 20;

const WeeklyMeetingMeals = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const [createSingleOpen, setCreateSingleOpen] = useState(false);
  const [createBulkOpen, setCreateBulkOpen] = useState(false);

  const [activeJob, setActiveJob] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const data = await fetchWeeklyMeetingConfig();
      setConfig(data);
    } catch {
      setConfig(null);
    } finally {
      setConfigLoading(false);
    }
  };

  const loadItems = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await fetchAllMeetingMeals({
        pageNumber: page,
        pageSize: PAGE_SIZE,
        status: statusFilter !== '' ? Number(statusFilter) : null,
        searchTerm: periodFilter ? null : (searchTerm || null),
        period: periodFilter || null,
      });
      const list = Array.isArray(data) ? data : [];
      setItems(list);
      setHasNextPage(list.length >= PAGE_SIZE);
      if (list.length === 0 && page > 1) {
        setPage((p) => p - 1);
      }
    } catch (e) {
      setError(e?.message || 'Yemek borçları yüklenemedi');
      setItems([]);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  };

  const pollActiveJob = (jobId) => {
    stopPolling();
    const tick = async () => {
      try {
        const status = await fetchBulkCreateMealsStatus(jobId);
        setActiveJob(status);
        if (status.state === 2 || status.state === 3) {
          stopPolling();
          localStorage.removeItem(BULK_MEALS_JOB_STORAGE_KEY);
          if (status.state === 2) {
            toast.success('Toplu yemek borçlandırma tamamlandı');
            loadItems();
          }
          setTimeout(() => setActiveJob(null), 5000);
        }
      } catch {
        stopPolling();
        localStorage.removeItem(BULK_MEALS_JOB_STORAGE_KEY);
        setActiveJob(null);
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  useEffect(() => {
    loadConfig();
    const savedJobId = localStorage.getItem(BULK_MEALS_JOB_STORAGE_KEY);
    if (savedJobId) pollActiveJob(savedJobId);
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, periodFilter, searchTerm]);

  const handleResetFilters = () => {
    setStatusFilter('');
    setPeriodFilter('');
    setSearchTerm('');
    setPage(1);
  };

  if (loading && items.length === 0 && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={1}>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>Haftalık Toplantılar</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setConfigDialogOpen(true)}
            sx={{ textTransform: 'none', borderColor: '#e6e6e6', color: '#444' }}
          >
            Ayarlar
          </Button>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => setCreateSingleOpen(true)}
            sx={{ textTransform: 'none', borderColor: '#e6e6e6', color: '#444' }}
          >
            Tek Üye
          </Button>
          <Button
            variant="contained"
            startIcon={<GroupAddIcon />}
            onClick={() => setCreateBulkOpen(true)}
            sx={{
              textTransform: 'none',
              backgroundColor: '#E7EDF4',
              boxShadow: 'none',
              color: '#0E4D94',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#CFDBEA', boxShadow: 'none' },
            }}
          >
            Toplu Oluştur
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ border: 'none', boxShadow: 'none', mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#444', mb: 0.5 }}>Yemek Birim Fiyatı</Typography>
            {configLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#0E4D94' }}>
                {config?.defaultMealPrice != null
                  ? formatMealMoney(config.defaultMealPrice, config.currency)
                  : 'Tanımlı değil — ayarlardan giriniz'}
              </Typography>
            )}
          </Box>
          <Alert severity="info" sx={{ flex: 1, fontSize: '13px' }}>
            Otomatik borçlar (katılım, eş/misafir) yoklama sonrası backend tarafından oluşturulur.
          </Alert>
        </Stack>
      </Paper>

      {activeJob && activeJob.state === 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Toplu yemek borçlandırma devam ediyor: {activeJob.processedCount}/{activeJob.totalMembers} işlendi
        </Alert>
      )}

      <Paper variant="outlined" sx={{ border: 'none', boxShadow: 'none', mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
          <Box sx={{ minWidth: 180 }}>
            <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Dönem</Typography>
            <TextField
              fullWidth
              size="small"
              type="month"
              value={periodFilter}
              onChange={(e) => { setPeriodFilter(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ minWidth: 250, flex: 1 }}>
            <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Ara (Dönem / Başlık)</Typography>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              placeholder="Dönem veya başlık ile ara..."
              disabled={!!periodFilter}
            />
          </Box>
          <Box sx={{ minWidth: 180 }}>
            <Typography sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px' }}>Durum</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <MenuItem value="">Tümü</MenuItem>
              {Object.entries(MeetingMealStatusNames).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Button onClick={handleResetFilters} sx={{ textTransform: 'none', height: 40 }}>Sıfırla</Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.05)', border: '1px solid #f5f5f5', py: 1, px: 2.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Üye</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Dönem</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Başlık</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Tutar</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Vade</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Durum</TableCell>
              <TableCell sx={{ color: '#a3a3a3', fontWeight: 500 }}>Oluşturulma</TableCell>
              <TableCell align="right" sx={{ color: '#a3a3a3', fontWeight: 500 }}>İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#a3a3a3' }}>
                  Kayıt bulunamadı
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const chipSx = getMeetingMealStatusChipSx(item.status);
                return (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/payments/weekly-meetings/${item.id}`)}
                  >
                    <TableCell>{item.memberName || '-'}</TableCell>
                    <TableCell>{formatPeriodLabel(item.period)}</TableCell>
                    <TableCell>{item.title || '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{formatMealMoney(item.amount, item.currency)}</TableCell>
                    <TableCell>{formatMealDate(item.dueDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.statusName || MeetingMealStatusNames[item.status]}
                        size="small"
                        sx={{ height: 24, fontSize: '11px', fontWeight: 500, borderRadius: '12px', ...chipSx }}
                      />
                    </TableCell>
                    <TableCell>{formatMealDate(item.createdAt)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Detay">
                        <IconButton size="small" onClick={() => navigate(`/payments/weekly-meetings/${item.id}`)}>
                          <VisibilityIcon sx={{ fontSize: 18, color: '#0E4D94' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {(page > 1 || hasNextPage) && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Button
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
            sx={{ textTransform: 'none' }}
          >
            Önceki
          </Button>
          <Typography sx={{ alignSelf: 'center', fontSize: '14px', color: '#666' }}>Sayfa {page}</Typography>
          <Button
            disabled={!hasNextPage || loading}
            onClick={() => setPage((p) => p + 1)}
            sx={{ textTransform: 'none' }}
          >
            Sonraki
          </Button>
        </Stack>
      )}

      <WeeklyMeetingConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        onUpdated={loadConfig}
      />
      <CreateMemberMealDialog
        open={createSingleOpen}
        onClose={() => setCreateSingleOpen(false)}
        onCreated={() => loadItems()}
      />
      <BulkCreateMealsDialog
        open={createBulkOpen}
        onClose={() => setCreateBulkOpen(false)}
        onCompleted={() => loadItems()}
      />
    </Box>
  );
};

export default WeeklyMeetingMeals;
